import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { default as React, useState } from "react";
import { ArrayField, BooleanField, ChipField, Datagrid, DateField, FunctionField, Labeled, NumberField, ReferenceArrayField, ReferenceField, SelectField, Show, SimpleShowLayout, SingleFieldList, TextField, UrlField, useDataProvider, useListContext, useNotify, usePermissions, useRecordContext, useRefresh } from "react-admin";
import { formatDrawingStatus } from "./DrawingList";
import { AlterValueDrawing } from "../../models";
import { PERMISSIONS } from "../../constants";

enum LuckyNumbersStatus {
    pending = 'pending',
    generating = 'generating',
    completed = 'completed',
    error = 'error',
}

const luckyNumbersStatusMap: Record<LuckyNumbersStatus, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
    [LuckyNumbersStatus.pending]: { label: 'Pendente', color: 'default' },
    [LuckyNumbersStatus.generating]: { label: 'Gerando...', color: 'warning' },
    [LuckyNumbersStatus.completed]: { label: 'Gerados', color: 'success' },
    [LuckyNumbersStatus.error]: { label: 'Erro', color: 'error' },
};

const StationField = (props) => {
    const { data } = useListContext();

    return (
        data && data.length ?
            <SingleFieldList>
                <ChipField source="name" emptyText="Todos" />
            </SingleFieldList>
            :
            <Typography variant="body2">Todos</Typography>
    );
}

const LuckyNumbersStatusAlert = () => {
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const isAdmin = permissions && permissions.includes(PERMISSIONS.ADMIN);

    if (record?.luckyNumbersStatus === LuckyNumbersStatus.generating) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                Os números da sorte estão sendo gerados. Isso pode levar alguns minutos.
            </Alert>
        );
    }

    if (record?.luckyNumbersStatus === LuckyNumbersStatus.error) {
        const handleRetry = async () => {
            if (!window.confirm('Deseja continuar a geração dos números da sorte de onde parou?')) return;

            setLoading(true);
            try {
                await dataProvider.create(`chains/${record.chainId}/drawings/${record.id}/retry-lucky-numbers`, { data: {} });
                notify('Regeneração dos números da sorte iniciada.', { type: 'success' });
                refresh();
            } catch (e: any) {
                notify(e?.message || 'Erro ao retentar geração dos números.', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        return (
            <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={isAdmin && (
                    <Button
                        color="inherit"
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={handleRetry}
                        disabled={loading}
                    >
                        {loading ? 'Retentando...' : 'Retentar'}
                    </Button>
                )}
            >
                Ocorreu um erro ao gerar os números da sorte.{!isAdmin && ' Entre em contato com o suporte.'}
            </Alert>
        );
    }

    return null;
};

const ArchiveAlert = () => {
    const record = useRecordContext();

    if (record?.isArchived) {
        const archivedDate = record.archivedAt
            ? new Date(record.archivedAt).toLocaleDateString('pt-BR')
            : '-';
        const total = record.totalLuckyNumbers?.toLocaleString('pt-BR') ?? '-';
        const assigned = record.totalAssignedNumbers?.toLocaleString('pt-BR') ?? '-';
        const participants = record.totalParticipants?.toLocaleString('pt-BR') ?? '-';

        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                Dados arquivados em {archivedDate}. Total de números: {total}. Atribuídos a clientes: {assigned}. Participantes: {participants}.
            </Alert>
        );
    }

    if (record?.status === 'drawn') {
        return (
            <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
                Os dados detalhados dos números da sorte são mantidos por até 1 ano após a data do sorteio.
            </Alert>
        );
    }

    return null;
};

const DrawingShow = (props) => (
    <Show {...props} title="Detalhes do sorteio">
        <SimpleShowLayout>
            <LuckyNumbersStatusAlert />
            <ArchiveAlert />
            <TextField label="Nome" source="name" />

            <Box display="flex" style={{ gap: 32 }}>
                <Labeled label="Data inicial">
                    <DateField source="startDate" locales="pt-BR" />
                </Labeled>
                <Labeled label="Data final">
                    <DateField source="endDate" locales="pt-BR" />
                </Labeled>
                <Labeled label="Data do sorteio">
                    <DateField source="drawingDate" showTime locales="pt-BR" />
                </Labeled>
            </Box>

            <Box display="flex" style={{ gap: 32 }}>
                <Labeled label="Número inicial">
                    <NumberField source="startNumber" />
                </Labeled>
                <Labeled label="Número final">
                    <NumberField source="endNumber" />
                </Labeled>
            </Box>

            <NumberField
                source="winningNumber"
                label="Número Sorteado"
                emptyText="Nenhum"
            />

            <ReferenceField
                label="Vencedor"
                source="winningCustomerId"
                reference="customers"
                link="show"
                emptyText="Nenhum"
            >
                <TextField source="name" />
            </ReferenceField>

            <FunctionField
                source="status"
                label="Status"
                render={record => formatDrawingStatus(record ? record.status : null)}
            />

            <FunctionField
                source="luckyNumbersStatus"
                label="Números da Sorte"
                render={(record) => {
                    const status = luckyNumbersStatusMap[record?.luckyNumbersStatus as LuckyNumbersStatus] || luckyNumbersStatusMap[LuckyNumbersStatus.pending];
                    const total = record ? (record.endNumber - record.startNumber + 1) : 0;
                    const count = record?.luckyNumbersCount ?? null;
                    return (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Chip label={status.label} color={status.color} size="small" />
                            {count !== null && (
                                <Typography variant="body2" color="text.secondary">
                                    ({count.toLocaleString('pt-BR')} / {total.toLocaleString('pt-BR')} gerados)
                                </Typography>
                            )}
                        </Box>
                    );
                }}
            />

            <UrlField source="drawingRegulationUrl" label="Url do regulamento" />

            <ArrayField source="drawingRuleFuels" label="Regras do sorteio">
                <Datagrid bulkActionButtons={false}>
                    <ReferenceField
                        source="drawingRuleId"
                        reference="drawing-rules"
                        label="Regra"
                        link={false}
                    >
                        <TextField source="label" />
                    </ReferenceField>
                    <ReferenceField
                        source="fuelId"
                        reference="fuels"
                        label="Combustível"
                        emptyText="Todos"
                        link={false}
                    >
                        <TextField source="name" />
                    </ReferenceField>
                    <SelectField
                        label="Tipo"
                        source="type"
                        choices={AlterValueDrawing.alterValueDrawingChoices}
                        emptyText="-"
                        textAlign="center"
                    />
                    <FunctionField
                        label="Quantidade"
                        textAlign="center"
                        render={(record) => {
                            if (record.type === AlterValueDrawing.value) {
                                return (
                                    <NumberField
                                        source="value"
                                        emptyText="R$ 0,00"
                                        options={{ style: 'currency', currency: 'BRL' }}
                                    />
                                )
                            } else {
                                return (
                                    <NumberField
                                        source="value"
                                        emptyText="0,00"
                                        options={{
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }}
                                    />
                                )
                            }
                        }}
                    />
                    <NumberField source="luckyNumberAmount" label="Quantidade de cupons" textAlign="center" />
                    <BooleanField label="Acumulado" source="accumulated" textAlign="center" emptyText="-" />
                    <DateField source="startDate" label="Data inicial" emptyText="-" textAlign="center" locales="pt-BR" />
                    <DateField source="endDate" label="Data final" emptyText="-" textAlign="center" locales="pt-BR" />
                    <ReferenceArrayField reference="stations" label="Postos" source="stationIds" emptyText="Todos">
                        <StationField />
                    </ReferenceArrayField>
                    <BooleanField label="Ativo" source="isEnabled" />
                </Datagrid>
            </ArrayField>
        </SimpleShowLayout>
    </Show>
);

export default DrawingShow;